import { paxtonHighlightAreaSchema } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { UserFileViewableContentSchema } from '@/uppy-file-upload/schemas'
import { z } from 'zod'

// AgentReferenceType enum
export enum AgentReferenceType {
  FILE_UPLOAD_CHUNK = 'file_upload_chunk',
  USER_FILE_QDRANT_CHUNK = 'user_file_qdrant_chunk',
  FILE_UPLOAD_FULL = 'file_upload_full',
  LRR = 'lrr',
  CASELAW = 'caselaw',
  TRELLIS_WRITING_SAMPLE = 'trellis_writing_sample',
  // Living-asset event citations (timeline / med chron). Real reference rows storing only a
  // pointer; event data (filename, page, highlights) is joined live from the asset tables.
  ASSET_EVENT = 'asset_event',
}
export const AgentReferenceTypeSchema = z.nativeEnum(AgentReferenceType)

// Base Reference Metadata Schema
export const BaseReferenceMetadataSchema = z.object({
  reference_id: z.string().uuid(),
  user_id: z.string(),
  file_path: z.string(),
  // Optional forward-compat fields for future user_files reference routing.
  // TODO(user_files-references): replace with finalized backend reference contract.
  storage_ref_full_path: z.string().optional(),
  storage_path: z.string().optional(),
  storage_bucket: z.string().optional(),
  bucket: z.string().optional(),
  reference_origin: z.string().optional(),
  source_system: z.string().optional(),
  storage_system: z.string().optional(),
  is_user_files_reference: z.boolean().optional(),
})

// ReferenceMetadataFileUploadChunkRead - legacy file upload pipeline.
export const ReferenceMetadataFileUploadChunkReadSchema = BaseReferenceMetadataSchema.extend({
  chunk_id: z.string(),
  page_nums: z.array(z.number()),
})
export type ReferenceMetadataFileUploadChunkRead = z.infer<typeof ReferenceMetadataFileUploadChunkReadSchema>

// ReferenceMetadataUserFileQdrantChunkRead - new user files pipeline.
export const ReferenceMetadataUserFileQdrantChunkReadSchema = BaseReferenceMetadataSchema.extend({
  chunk_id: z.string(),
  page_nums: z.array(z.number()),
  filename: z.string(),
  user_file_id: z.string().uuid(),
})
export type ReferenceMetadataUserFileQdrantChunkRead = z.infer<typeof ReferenceMetadataUserFileQdrantChunkReadSchema>

// ReferenceMetadataFileUploadFullRead
export const ReferenceMetadataFileUploadFullReadSchema = BaseReferenceMetadataSchema.extend({
  // No extensions
})
export type ReferenceMetadataFileUploadFullRead = z.infer<typeof ReferenceMetadataFileUploadFullReadSchema>

// ReferenceMetadataCaselawWithStatus - mirrors the backend `ReferenceMetadataCaselawWithStatus`.
// `case_status` is `null` for caselaw refs with un-parseable parent_id (rare).
export const ReferenceMetadataCaselawWithStatusSchema = z
  .object({
    reference_id: z.string().uuid(),
    chunk_id: z.string(),
    s3_dir: z.string(),
    title: z.string(),
    bb_citation: z.string(), // Bluebook citation; preferred for display
    citation: z.string().nullable(),
    docket: z.string().nullable(),
    jurisdiction_code: z.string(),
    decide_date: z.string(),
    parent_id: z.string(),
    case_status: z.string().nullable().optional(),
  })
  .transform((data) => ({
    ...data,
    // Aligned with ReferenceMetadataLrrReadSchema
    s3_path: data.s3_dir ?? '',
  }))
export type ReferenceMetadataCaselawWithStatus = z.infer<typeof ReferenceMetadataCaselawWithStatusSchema>

// ReferenceMetadataLrrRead - based on new backend ReferenceMetadataLRR model
export const ReferenceMetadataLrrReadSchema = z
  .object({
    reference_id: z.string().uuid(),
    chunk_id: z.string(),
    s3_dir: z.string(),
    slug: z.string(),
    name: z.string(),
    citation: z.string().nullable(),
    jurisdiction_code: z.string(),
    law_type: z.string(), // statute, regulation, rule
  })
  .transform((data) => ({
    ...data,
    // Aligned with ReferenceMetadataCaselawWithStatusSchema
    s3_path: data.s3_dir ?? '',
  }))
export type ReferenceMetadataLrrRead = z.infer<typeof ReferenceMetadataLrrReadSchema>

// Living-asset event citations (timeline / med chron); event data is joined live from the asset tables.
export const ReferenceMetadataAssetEventReadSchema = z.object({
  reference_id: z.string().uuid(),
  asset_kind: z.enum(['TIMELINE', 'MED_CHRON']),
  event_id: z.string().uuid(),
  user_id: z.string(),
  user_file_id: z.string().uuid(),
  filename: z.string(),
  page_nums: z.array(z.number()).default([]),
})
export type ReferenceMetadataAssetEventRead = z.infer<typeof ReferenceMetadataAssetEventReadSchema>

// Writing samples retrieved by the agent to guide drafted documents.
export const ReferenceMetadataWritingSampleReadSchema = z.object({
  reference_id: z.string().uuid(),
  display_group_key: z.string(),
  title: z.string().nullable(),
  court: z.string().nullable(),
  jurisdiction: z.string().nullable(),
  filed_date: z.string().nullable(),
})
export type ReferenceMetadataWritingSampleRead = z.infer<typeof ReferenceMetadataWritingSampleReadSchema>

/**
 * Agent Highlighted Citation Schema
 *
 * Represents a single citation occurrence with its associated highlights.
 *
 * A single reference can have multiple highlighted_citations entries when it's cited
 * multiple times in one agent response. Each entry represents a distinct citation
 * occurrence at a specific position in the response.
 *
 * Example: If response contains "According to [ref_1], X applies. However, [ref_1] also states Y."
 * - First [ref_1] → highlighted_citation with index=0
 * - Second [ref_1] → highlighted_citation with index=1
 *
 * Fields:
 * - reference_id: UUID of the referenced document
 * - event_id: UUID of the agent response event containing this citation
 * - index: Zero-based ordinal position of this citation occurrence within the response
 *           (0 for first occurrence, 1 for second, etc.)
 * - sentences: Array of sentence strings to highlight (used for HTML/Markdown rendering)
 *              Same reference might highlight different sentences depending on context
 * - highlights: Array of PDF highlight areas (used for PDF rendering)
 *               Contains page numbers and bounding box coordinates
 *
 * Note: sentences and highlights are both nullable because:
 * - HTML/Markdown references use sentences (highlights is null)
 * - PDF references use highlights (sentences is null)
 */
export const AgentHighlightedCitationSchema = z.object({
  reference_id: z.string().uuid(),
  event_id: z.string().uuid(),
  index: z.number(),
  sentences: z.array(z.string()).nullable(),
  highlights: z.array(paxtonHighlightAreaSchema).nullable(),
})
export type AgentHighlightedCitation = z.infer<typeof AgentHighlightedCitationSchema>

export const ClientFileReferenceSchema = z.object({
  user_file_id: z.string().uuid(),
  filename: z.string(),
  page_nums: z.array(z.number()).default([]),
  viewable_content: UserFileViewableContentSchema.nullish().default(null),
})
export type ClientFileReference = z.infer<typeof ClientFileReferenceSchema>
export const ClientFileReferenceOrNullSchema = ClientFileReferenceSchema.nullish().default(null).catch(null)

// AgentReferenceRead
export const AgentReferenceReadSchema = z.object({
  id: z.string().uuid(),
  turn_id: z.string().uuid(),
  type: AgentReferenceTypeSchema,
  citation_id: z.string(),
  created_at: z.string().datetime(),
})
export type AgentReferenceRead = z.infer<typeof AgentReferenceReadSchema>

const ClientAgentReferenceBaseSchema = z.object({
  conversation_id: z.string().uuid(),
  reference: AgentReferenceReadSchema,
  highlighted_citations: z.array(AgentHighlightedCitationSchema),
  file_reference: ClientFileReferenceOrNullSchema,
})

/**
 * Client Agent Reference
 * - the full reference object
 * - discriminated union of the two types of references
 * - Docs https://zod.dev/?id=discriminated-unions
 *
 * Discriminated unions allow us to access the correct type of metadata based on
 * the "type" field of the reference object.
 *
 * @example
 * switch (reference.type) {
 *  case AgentReferenceType.FILE_UPLOAD_CHUNK: {
 *    const pageNumbers = reference.metadata.page_nums // IDE property access of unique metadata fields works correctly.
 *    break
 *  }
 */
// ClientAgentReference - the full reference object
// This is a discriminated union of the two types of references
export const ClientAgentReferenceSchema = z.discriminatedUnion('type', [
  ClientAgentReferenceBaseSchema.extend({
    type: z.literal(AgentReferenceType.FILE_UPLOAD_CHUNK),
    metadata: ReferenceMetadataFileUploadChunkReadSchema,
  }),
  ClientAgentReferenceBaseSchema.extend({
    type: z.literal(AgentReferenceType.USER_FILE_QDRANT_CHUNK),
    metadata: ReferenceMetadataUserFileQdrantChunkReadSchema,
  }),
  ClientAgentReferenceBaseSchema.extend({
    type: z.literal(AgentReferenceType.FILE_UPLOAD_FULL),
    metadata: ReferenceMetadataFileUploadFullReadSchema,
  }),
  ClientAgentReferenceBaseSchema.extend({
    type: z.literal(AgentReferenceType.LRR),
    metadata: ReferenceMetadataLrrReadSchema,
  }),
  ClientAgentReferenceBaseSchema.extend({
    type: z.literal(AgentReferenceType.CASELAW),
    metadata: ReferenceMetadataCaselawWithStatusSchema,
  }),
  ClientAgentReferenceBaseSchema.extend({
    type: z.literal(AgentReferenceType.TRELLIS_WRITING_SAMPLE),
    metadata: ReferenceMetadataWritingSampleReadSchema,
  }),
  ClientAgentReferenceBaseSchema.extend({
    type: z.literal(AgentReferenceType.ASSET_EVENT),
    metadata: ReferenceMetadataAssetEventReadSchema,
  }),
])
export type ClientAgentReference = z.infer<typeof ClientAgentReferenceSchema>

export const ReferenceMetadataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(AgentReferenceType.FILE_UPLOAD_CHUNK),
    metadata: ReferenceMetadataFileUploadChunkReadSchema,
  }),
  z.object({
    type: z.literal(AgentReferenceType.USER_FILE_QDRANT_CHUNK),
    metadata: ReferenceMetadataUserFileQdrantChunkReadSchema,
  }),
  z.object({
    type: z.literal(AgentReferenceType.FILE_UPLOAD_FULL),
    metadata: ReferenceMetadataFileUploadFullReadSchema,
  }),
  z.object({
    type: z.literal(AgentReferenceType.LRR),
    metadata: ReferenceMetadataLrrReadSchema,
  }),
  z.object({
    type: z.literal(AgentReferenceType.CASELAW),
    metadata: ReferenceMetadataCaselawWithStatusSchema,
  }),
  z.object({
    type: z.literal(AgentReferenceType.TRELLIS_WRITING_SAMPLE),
    metadata: ReferenceMetadataWritingSampleReadSchema,
  }),
  z.object({
    type: z.literal(AgentReferenceType.ASSET_EVENT),
    metadata: ReferenceMetadataAssetEventReadSchema,
  }),
])
export type AgentReferenceMetadata = z.infer<typeof ReferenceMetadataSchema>

// Agent Reference HTML Response Schema
// Used for fetching HTML content for Caselaw and LRR references
export const AgentReferenceHtmlResponseSchema = z.object({
  data: z.string(),
})
export type AgentReferenceHtmlResponse = z.infer<typeof AgentReferenceHtmlResponseSchema>
